/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React, { Component } from 'react';
import { OrderInformation } from 'src/adhoc/store/types/MetadataTypes';
import { IMultiAxisDataset } from '../../../../store/types/MultiAxisDatasetTypes';

interface ICrosstabLevelsPart {
    levelsStructure: {
        rows: Array<any>,
        columns: Array<any>
    },
    dataset: IMultiAxisDataset,
    headerWidths: number[],

    onLevelExpansionChange: Function
}

function isExpanded(column: any, items: Array<any>) {
    return items.find((item: any) => (item.id ? item.id === column.id : item.field === column.field)).expanded;
}

function isExpandable(column: any) {
    return column.kind !== 'measures' && column.includeAll
}

function removedSummariesShift(line: any, lines: any[]) {
    let index = lines.indexOf(line) + 1;
    let res = line;

    while (lines[index] && !lines[index].includeAll) {
        res = lines[index];
        index += 1;
    }

    return res;
}

function collapser(line: any, lines: any[], items: any[], handler: Function, columns: boolean) {
    let res = null;

    if (isExpandable(line) && line !== lines[0]) {
        if (isExpanded(line, items)) {
            res = <span role="presentation" className="jr-mDatatable-cell-expander jr-mIcon jr jr-isExpanded" onClick={() => { handler(line, false, columns) }} />
        } else {
            res = (
                <span
                    role="presentation"
                    className="jr-mDatatable-cell-expander jr-mIcon jr jr-isCollapsed"
                    onClick={() => {
                        handler(removedSummariesShift(line, lines), true, columns)
                    }}
                />
            )
        }
    }

    return res;
}

function sortingIconFactory(sorting: OrderInformation | undefined, level: any) {
    if (!sorting) {
        return null;
    }

    let sort: any;
    if (level.measures) {
        sort = sorting.levels.find((levelSorting) => levelSorting.aggregation);
    } else {
        sort = sorting.levels.find((levelSorting) => (
            (level.hierarchicalName === levelSorting.fieldRef) && !levelSorting.aggregation
        ));
    }
    if (!sort) {
        return null;
    }

    const sortingClassName = sort.ascending ? 'jr-sortAscending' : 'jr-sortDescending';
    return (
        <div
            className={`jr-mDatatable-cell-sort jr-mIcon jr-mIconSmall jr ${sortingClassName}`}
            style={{ height: '1em', lineHeight: '1' }}
        />
    );
}

export default class CrosstabLevelsPart extends Component<ICrosstabLevelsPart, object> {
    render() {
        const {
            levelsStructure: {
                rows,
                columns,
            },
            dataset: {
                summary
            },
            headerWidths,
            onLevelExpansionChange
        } = this.props;

        const columnLevels = columns.map((column, index) => (
            <tr key={column.id || ''} className="jr-mDatatable-row jr">
                <th
                    className="jr-mDatatable-cell jr jr-mDatatable-cellField jr-mDatatable-cellRowlabel"
                    colSpan={rows.length}
                    title={column.label}
                    data-local-index={index}
                >
                    <div className="jr-mDatatable-cell-wrapper jr">
                        <div className="jr-mDatatable-cell-text jr">
                            {collapser(column, columns, summary.columns.items, onLevelExpansionChange, true)}
                            {column.label}
                            {sortingIconFactory(summary.order, column)}
                        </div>
                    </div>
                </th>
            </tr>
        ));

        const rowLevels = rows.map((row, index) => (
            <th
                key={row.id || ''}
                className="jr-mDatatable-cell jr jr-mDatatable-cellField jr-mDatatable-cellColumnlabel"
                title={row.label}
                data-local-index={index}
                style={{ minWidth: headerWidths[index] }}
            >
                <div className="jr-mDatatable-cell-wrapper jr">
                    <div className="jr-mDatatable-cell-text jr">
                        {collapser(row, rows, summary.rows.items, onLevelExpansionChange, false)}
                        {row.label}
                        {sortingIconFactory(summary.order, row)}
                    </div>
                </div>
            </th>
        ));

        return (
            <table className="jr-mDatatable jr-mDatatableCrosstab jr" style={{ position: 'relative' }}>
                <thead className="jr-mDatatable-header jr">
                    {columnLevels}
                    <tr key={0} className="jr-mDatatable-row jr jr-mDatatable-rowGrouplabels">
                        {rowLevels}
                    </tr>
                </thead>
            </table>
        )
    }
}
